import CartApiService, { type AddItemInput, CartError } from './CartApiService'
import { type Cart, type CartItem, EMPTY_CART } from './CartStorageService'
import ToastService from './ToastService'

export const CART_CHANGE_EVENT = 'cart:change'
export const CART_EMPTIED_EVENT = 'cart:emptied'
export const CART_EXPIRED_EVENT = 'cart:expired'
export const CART_EXPIRING_EVENT = 'cart:expiring'

export interface CartChangeDetail {
  expiresAt: number
  items: CartItem[]
  totalAmount: string
}

export interface CartExpiringDetail {
  expiresAt: number
}

// The seat-reservation warning opens this long before the cart's server-side TTL lapses.
export const WARNING_LEAD_MS = 5 * 60 * 1000

class CartService {
  private cart: Cart = EMPTY_CART
  private expiryTimer: null | number = null
  private warningTimer: null | number = null

  async addItem(input: AddItemInput): Promise<boolean> {
    return this.mutate(async (): Promise<Cart> => CartApiService.addItem(input))
  }

  dispatchEmptied(): void {
    document.dispatchEvent(new CustomEvent(CART_EMPTIED_EVENT))
  }

  dispatchExpired(): void {
    document.dispatchEvent(new CustomEvent(CART_EXPIRED_EVENT))
  }

  async extendReservation(): Promise<boolean> {
    const cart = await CartApiService.extendReservation()
    if (cart === null) {
      this.persist(EMPTY_CART)
      this.dispatchExpired()
      return false
    }

    this.persist(cart)
    return true
  }

  getCart(): Cart {
    return this.cart
  }

  getCount(): number {
    return this.cart.items.length
  }

  async hydrate(): Promise<void> {
    const cart = await CartApiService.getCart()
    this.persist(cart)
  }

  // Owns the expiry lifecycle. Called once from the single Cart entry bundle; re-arms the
  // warn/expire timers from every cart:change (each carries the authoritative expiresAt), so
  // a refresh triggered in any bundle re-arms correctly despite per-bundle CartService copies.
  initLifecycle(): void {
    document.addEventListener(CART_CHANGE_EVENT, (event: Event): void => {
      const detail = (event as CustomEvent<CartChangeDetail>).detail
      this.armExpiryTimers(detail.expiresAt, detail.items.length)
    })
  }

  async removeItem(itemId: string): Promise<boolean> {
    return this.mutate(async (): Promise<Cart> => CartApiService.removeItem(itemId))
  }

  async updateItem(itemId: string, quantity: number): Promise<boolean> {
    return this.mutate(async (): Promise<Cart> => CartApiService.updateItem(itemId, quantity))
  }

  private armExpiryTimers(expiresAt: number, itemCount: number): void {
    this.clearExpiryTimers()
    if (itemCount === 0 || expiresAt === 0) return

    const msUntilExpiry = expiresAt - Date.now()
    if (msUntilExpiry <= 0) {
      this.expire()
      return
    }

    const msUntilWarning = msUntilExpiry - WARNING_LEAD_MS
    if (msUntilWarning <= 0) {
      this.dispatchExpiring(expiresAt)
    } else {
      this.warningTimer = window.setTimeout((): void => this.dispatchExpiring(expiresAt), msUntilWarning)
    }

    this.expiryTimer = window.setTimeout((): void => this.expire(), msUntilExpiry)
  }

  private clearExpiryTimers(): void {
    if (this.warningTimer !== null) {
      window.clearTimeout(this.warningTimer)
      this.warningTimer = null
    }
    if (this.expiryTimer !== null) {
      window.clearTimeout(this.expiryTimer)
      this.expiryTimer = null
    }
  }

  private dispatch(): void {
    const event = new CustomEvent<CartChangeDetail>(CART_CHANGE_EVENT, {
      detail: { expiresAt: this.cart.expiresAt, items: this.cart.items, totalAmount: this.cart.totalAmount },
    })
    document.dispatchEvent(event)
  }

  private dispatchExpiring(expiresAt: number): void {
    document.dispatchEvent(new CustomEvent<CartExpiringDetail>(CART_EXPIRING_EVENT, { detail: { expiresAt } }))
  }

  private expire(): void {
    this.persist(EMPTY_CART)
    this.dispatchExpired()
  }

  private async mutate(action: () => Promise<Cart>): Promise<boolean> {
    try {
      const cart = await action()
      this.persist(cart)
      return true
    } catch (error) {
      if (error instanceof CartError) {
        if (error.code === 'cart_expired') {
          this.persist(EMPTY_CART)
          this.dispatchExpired()
          return false
        }

        if (error.code === 'duplicate_item') {
          ToastService.showFromElement('cart-toast-duplicate')
          return false
        }

        if (error.code === 'max_participants_exceeded') {
          ToastService.showFromElement('cart-toast-max-participants')
          return false
        }

        if (error.message !== '') {
          ToastService.error(error.message)
          return false
        }
      }

      ToastService.showFromElement('cart-toast-error')
      return false
    }
  }

  private persist(cart: Cart): void {
    this.cart = cart
    this.dispatch()
  }
}

export default new CartService()
